/**
 * Syncs a progress thumb with a horizontal scroll container.
 * Used under homepage carousels and Instafeed on mobile.
 */
(function () {
  if (customElements.get('ea-horizontal-scroll-indicator')) return;

  const DEFAULT_TARGETS = 'slideshow-slides, .instafeed-new-layout-container';

  class EaHorizontalScrollIndicator extends HTMLElement {
    /** @type {HTMLElement | null} */
    #scroller = null;

    /** @type {HTMLElement | null} */
    #thumb = null;

    /** @type {ResizeObserver | null} */
    #resizeObserver = null;

    /** @type {MutationObserver | null} */
    #mutationObserver = null;

    /** @type {number} */
    #bindAttempts = 0;

    connectedCallback() {
      this.#thumb = this.querySelector('.ea-horizontal-scroll-indicator__thumb');
      if (!this.#thumb) return;

      this.#bindScroller();

      if (!this.#scroller) {
        this.#scheduleBindRetry();
      }
    }

    disconnectedCallback() {
      this.#scroller?.removeEventListener('scroll', this.#update);
      this.#resizeObserver?.disconnect();
      this.#mutationObserver?.disconnect();
      this.#resizeObserver = null;
      this.#mutationObserver = null;
      this.#scroller = null;
      this.#bindAttempts = 0;
    }

    #scheduleBindRetry() {
      if (this.#bindAttempts >= 20) return;

      this.#bindAttempts += 1;
      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        this.#bindScroller();

        if (!this.#scroller) {
          this.#scheduleBindRetry();
        }
      });
    }

    #bindScroller() {
      const selector = this.dataset.target || DEFAULT_TARGETS;
      const root = this.parentElement;

      if (!root) return;

      const scroller = root.querySelector(selector);

      if (!scroller || scroller === this.#scroller) {
        if (scroller && scroller === this.#scroller) this.#update();
        return;
      }

      this.#scroller?.removeEventListener('scroll', this.#update);
      this.#resizeObserver?.disconnect();
      this.#mutationObserver?.disconnect();

      this.#scroller = scroller;
      this.#scroller.addEventListener('scroll', this.#update, { passive: true });

      this.#resizeObserver = new ResizeObserver(this.#update);
      this.#resizeObserver.observe(this.#scroller);

      const slideshow = this.#scroller.closest('slideshow-component');
      if (slideshow) {
        this.#mutationObserver = new MutationObserver(this.#update);
        this.#mutationObserver.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport'] });
      }

      this.#update();
    }

    #update = () => {
      const scroller = this.#scroller;
      const thumb = this.#thumb;

      if (!scroller || !thumb) return;

      const { scrollWidth, clientWidth, scrollLeft } = scroller;

      if (scrollWidth <= clientWidth + 1) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      const thumbWidthPercent = (clientWidth / scrollWidth) * 100;
      const maxOffsetPercent = 100 - thumbWidthPercent;
      const scrollRatio = scrollLeft / (scrollWidth - clientWidth);
      const thumbOffsetPercent = maxOffsetPercent * scrollRatio;

      thumb.style.width = `${thumbWidthPercent}%`;
      thumb.style.marginInlineStart = `${thumbOffsetPercent}%`;
    };
  }

  customElements.define('ea-horizontal-scroll-indicator', EaHorizontalScrollIndicator);
})();
